import React from "react";
import { Box, Flex, Text, Button, keyframes } from "@chakra-ui/react";

const slideDown = keyframes`
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

/**
 * DemoNarrationBar — slim fixed bar at top for "watch this" narration steps.
 * No backdrop overlay — product stays fully visible and interactive underneath.
 *
 * Used for: POINTS_CELEBRATION (auto-dismiss, no button), RESULTS_COMMENTARY (has Continue button)
 */
const DemoNarrationBar = ({ isOpen, stepConfig, onAction, onEndDemo }) => {
  if (!isOpen || !stepConfig || stepConfig.modalType !== "narration_bar")
    return null;

  const hasButtons = stepConfig.buttons?.length > 0;

  return (
    <Box
      position="fixed"
      top="28px"
      left={0}
      right={0}
      zIndex={1300}
      px={4}
      py={3}
      bg="white"
      boxShadow="md"
      fontFamily="Geist, sans-serif"
      animation={`${slideDown} 0.3s ease-out`}
    >
      <Flex align="center" justify="space-between" maxW="960px" mx="auto">
        {/* Left: text content */}
        <Box flex={1} mr={4}>
          {stepConfig.heading && (
            <Text fontSize="sm" fontWeight="700" color="#0C001C">
              {stepConfig.heading}
            </Text>
          )}
          {stepConfig.subtitle && (
            <Text
              fontSize="lg"
              fontWeight="700"
              color="#4C47CC"
              animation={`${slideDown} 0.6s ease-out`}
            >
              {stepConfig.subtitle}
            </Text>
          )}
          {stepConfig.body && (
            <Text fontSize="xs" color="#6B7280" mt={0.5}>
              {stepConfig.body}
            </Text>
          )}
        </Box>

        {/* Right: action button + end demo */}
        <Flex direction="column" align="flex-end" gap={1} flexShrink={0}>
          {hasButtons &&
            stepConfig.buttons.map((btn) => (
              <Button
                key={btn.action}
                size="sm"
                bg={btn.variant === "primary" ? "#4C47CC" : "transparent"}
                color={btn.variant === "primary" ? "white" : "#6B7280"}
                fontWeight="700"
                fontSize="xs"
                borderRadius="8px"
                border={btn.variant === "secondary" ? "1px solid" : "none"}
                borderColor={
                  btn.variant === "secondary" ? "gray.300" : "transparent"
                }
                _hover={{
                  bg: btn.variant === "primary" ? "#3A37B3" : "#F3F4F6",
                }}
                onClick={() => onAction(btn.action)}
              >
                {btn.label}
              </Button>
            ))}
          <Text
            fontSize="xs"
            color="gray.400"
            cursor="pointer"
            _hover={{ color: "gray.600" }}
            onClick={onEndDemo}
          >
            End Demo
          </Text>
        </Flex>
      </Flex>
    </Box>
  );
};

export default DemoNarrationBar;
